import {  claimTask, releaseTask, startTask, pauseTask, startTaskBulk, pauseTaskBulk, releaseTaskBulk, fetchTask, fetchTasks } from '../store/slices/tasksReducer'
import { openModal } from '../store/slices/modalReducer'
import { selectTask, setTask } from '../store/slices/tasksReducer'

export const getActiveAssignments = (assignments) => {
  return assignments.reduce((acc, assignment, i) => {
    const newacc = [ ...acc ];
    if (assignment.status === "AssignmentStopped") return newacc;
    newacc.push(assignment);
    return newacc;
  }, []);
}

export const getActiveExecutors = (assignments) => {
  if(!assignments) return [];
  const activeAssignments = getActiveAssignments(assignments);
  return activeAssignments.map(assignment => assignment.todoAppUser.maccount);
}

export const getStartedExecutors = (assignments) => {
  if(!assignments) return [];
  const activeAssignments = getActiveAssignments(assignments);
  return activeAssignments
    .filter(assignment => assignment.status === "Started")
    .map(assignment => assignment.todoAppUser.maccount);
}

export const getCurrentAssignmentForUser = (assignments, userName) => {
  if(!assignments) return;
  return assignments.find((assignment, i) => {
    if (assignment.status === "AssignmentStopped") return false;
    if (assignment.todoAppUser.maccount.toLowerCase() === userName.toLowerCase()) {
      const foundStopped = assignments.find((a, index) => {
        const forUser = userName.toLowerCase() === a.todoAppUser.maccount.toLowerCase();
        const hasStopped = a.status === 'AssignmentStopped';
        const afterAssignment = assignment.id < a.id;
        return forUser && hasStopped && afterAssignment;
      });
      return !foundStopped;
    }
    return false;
  });
}

export const getCurrentAssignment = (assignments) => {
  if(!assignments) return;
  return assignments.find((assignment, i) => {
    if (assignment.status === "AssignmentStopped") return false;
    return true;
  });
}

export const hasAnyStartedAssignment = (assignments) => {
  if(!assignments) return false;
  const activeAssignments = getActiveAssignments(assignments);
  return activeAssignments.some(assignment => assignment.status === "Started");
}

export const isUserAssignedToTask = (task, userName) => {
  if (!task?.taskAssignments) return false;
  const activeAssignments = getActiveAssignments(task.taskAssignments);
  return activeAssignments.some(assignment => 
    assignment.todoAppUser.maccount.toLowerCase() === userName.toLowerCase()
  );
}

export const hasUserStoppedAssignment = (assignments, userName) => {
  if (!assignments) return false;
  return assignments.some(assignment => 
    assignment.status === "AssignmentStopped" &&
    assignment.todoAppUser.maccount.toLowerCase() === userName.toLowerCase()
  );
}

export const getActiveTasksForUser = (tasks, userName) => {
  const tasksWithAssignmentStatus = tasks.map((task) => {
    const taskWithAssignmentStatus = { ...task }
    if(task.taskAssignments) {
      const activeAssignments = getActiveAssignments(task.taskAssignments);
      activeAssignments.map((assignment) => {
        if (assignment.todoAppUser.maccount.toLowerCase() === userName.toLowerCase()) {
          taskWithAssignmentStatus.taskStatus = assignment.status;
          taskWithAssignmentStatus.assignedToCurrentUser = true;
        }
        return false;
      });
    }
    return taskWithAssignmentStatus;
  });

  return tasksWithAssignmentStatus.reduce((acc, task ) => {
    if(task.taskStatus) {
      acc.activeTasks.push(task);
    } else {
      acc.otherTasks.push(task);
    }
    return acc;
  }, { activeTasks: [], otherTasks: [] })
}

export const getAvailableActions = (task, user, loading, setLoading, dispatch, multipleTimeRegistrationsPerTaskActive = false) => {
    const userName = user.userName.toLowerCase();
    const taskStatus = task?.status;
    const currentAssignmentForUser = getCurrentAssignmentForUser(task?.taskAssignments, user.userName);
    const currentAssignment = getCurrentAssignment(task?.taskAssignments);
    const assignment = currentAssignmentForUser || currentAssignment;
    const isAssingedToCurrentUser = userName === assignment?.todoAppUser.maccount.toLowerCase();
    const enableBulkStart = multipleTimeRegistrationsPerTaskActive === true;
  return {
    complete: {
      disabled: ((loading !== 'COMPLETE') && loading !== false) || taskStatus === "Finished" || taskStatus === "Aborted",
      isLoading: loading === 'COMPLETE',
      visible: true,
      action: async () => {
        if(task?.formAndSurveyData?.requiredOnFinish === true && task?.formAndSurveyData?.templateId && !task.formAndSurveyData.responseJson) {
          await dispatch(setTask(task));
          return dispatch(openModal('Form'))
        }
        dispatch(selectTask(task.id));
        dispatch(openModal('CompleteTask'))
      },
    },
    stop: {
      disabled: ((loading !== 'STOP') && loading !== false) || taskStatus === "Finished" || taskStatus === "Aborted",
      isLoading: loading === 'STOP',
      visible: true,
      action: () => {
        dispatch(selectTask(task.id));
        dispatch(openModal('CancelTask'))
      },
    },
    start: {
      disabled: ((loading !== 'START') && loading !== false) || taskStatus === "Finished" || taskStatus === "Aborted",
      isLoading: loading === 'START',
      visible: !hasAnyStartedAssignment(task?.taskAssignments),
      action: async (id) => {
        if(task?.formAndSurveyData?.requiredOnStart === true && task?.formAndSurveyData?.templateId && !task.formAndSurveyData.responseJson) {
          await dispatch(setTask(task));
          return dispatch(openModal('Form'))
        }
        const taskId = (typeof id === 'number')? id : task.id;
        
        // If the domain does NOT allow multiple time registrations per task,
        // fall back to a simple single-user start flow without opening the bulk modal.
        if (!enableBulkStart) {
          try {
            if (typeof setLoading === 'function') {
              setLoading('START');
            }

            // Start task first - commit happens here
            const startResult = await dispatch(startTask(taskId));
            
            // Only claim/assign user AFTER successful start (commit already happened)
            if (startResult.type === 'task/startTask/fulfilled' && !isAssingedToCurrentUser) {
              try {
                await dispatch(claimTask(taskId));
              } catch (claimError) {
                console.error('Error claiming task after start:', claimError);
                // Log error but don't fail since task is already started
              }
            }
            
            await dispatch(fetchTask(taskId));
          } catch (error) {
            console.error('Error in non-bulk start action:', error);
            throw error;
          } finally {
            if (typeof setLoading === 'function') {
              setLoading(false);
            }
          }
          return;
        }

        try {
          
          // Use task.active to decide: never started -> open modal; previously started -> use existing team
          const activeExecutors = getActiveExecutors(task?.taskAssignments);
          
          if (!task?.active) {
            // Task has never been started before -> open modal to select team
            // Don't claim task yet - wait until user confirms "Start tijdsregistratie"
            // Refresh task data first to ensure we have the latest task info (including domainId)
            const refreshedTask = await dispatch(fetchTask(taskId));
            const currentTask = refreshedTask.payload || task;
            // Set task in Redux store before opening modal so StartTaskBulk can access it
            dispatch(setTask(currentTask));
            return dispatch(openModal('StartTaskBulk'));
          }

          // Task was started before (paused/resumed path) - use existing active executors
          //if (activeExecutors.length > 0) {
          //  setLoading('START');
          //  await dispatch(startTaskBulk({ taskId, mAccounts: activeExecutors }));
          //  await dispatch(fetchTask(taskId));
          //  setLoading(false);
          // return;
          //}

          // Fallback: if no active executors are present, open modal to allow selection
          // Refresh task data first to ensure we have the latest task info
          const refreshedTask = await dispatch(fetchTask(taskId));
          const currentTask = refreshedTask.payload || task;
          // Set task in Redux store before opening modal so StartTaskBulk can access it
          dispatch(setTask(currentTask));
          return dispatch(openModal('StartTaskBulk'));
        } catch (error) {
          console.error('Error in start action:', error);
          setLoading(false);
          throw error;
        }
      },
    },
    pause: {
      disabled: ((loading !== 'PAUSE') && loading !== false) || taskStatus === "Finished" || taskStatus === "Aborted",
      isLoading: loading === 'PAUSE',
      visible: hasAnyStartedAssignment(task?.taskAssignments),
      action: async (id) => {
        const taskId = (typeof id === 'number')? id : task.id;
        setLoading('PAUSE');
        if(task?.formAndSurveyData?.requiredOnHold === true && task?.formAndSurveyData?.templateId && !task.formAndSurveyData.responseJson) {
          await dispatch(setTask(task));
          return dispatch(openModal('Form'))
        }
        
        try {
          // Refresh task data to get the most current started executors and assignments
          const refreshedTask = await dispatch(fetchTask(taskId));
          const currentTask = refreshedTask.payload || task;
          
          // Recalculate assignment status based on refreshed task data (important after claim)
          const refreshedAssignmentForUser = getCurrentAssignmentForUser(currentTask?.taskAssignments, user.userName);
          const refreshedAssignment = refreshedAssignmentForUser || getCurrentAssignment(currentTask?.taskAssignments);
          const isUserAssignedToTask = userName === refreshedAssignment?.todoAppUser.maccount.toLowerCase();
          
          // Get all started executors (only those with status "Started" can be paused)
          const startedExecutors = getStartedExecutors(currentTask?.taskAssignments);
          console.log('Pausing task with started executors:', startedExecutors);
          console.log('User assigned to task:', isUserAssignedToTask);
          
          // If user is not assigned to the task, always use bulk pause with all started executors
          // Otherwise, use bulk pause if multiple executors, single pause if one executor
          if (!isUserAssignedToTask) {
            // User is not linked to the task - use bulk pause with all started executors
            if (startedExecutors.length === 0) {
              console.warn('No started executors found to pause');
              setLoading(false);
              return;
            }
            return dispatch(pauseTaskBulk({ taskId, mAccounts: startedExecutors })).then(() => {
              return dispatch(fetchTask(taskId)).then(() => {
                setLoading(false);
              })
            })
          } else if (startedExecutors.length > 1) {
            // User is assigned and there are multiple started executors - use bulk pause
            return dispatch(pauseTaskBulk({ taskId, mAccounts: startedExecutors })).then(() => {
              return dispatch(fetchTask(taskId)).then(() => {
                setLoading(false);
              })
            })
          } else {
            // User is assigned and there's only one executor - use single pause
            return dispatch(pauseTask(taskId)).then(() => {
              return dispatch(fetchTask(taskId)).then(() => {
                setLoading(false);
              })
            })
          }
        } catch (error) {
          console.error('Error refreshing task data before pause:', error);
          // Fallback to using the original task data if refresh fails
          const startedExecutors = getStartedExecutors(task?.taskAssignments);
          
          // Recalculate assignment status based on original task data as fallback
          const fallbackAssignmentForUser = getCurrentAssignmentForUser(task?.taskAssignments, user.userName);
          const fallbackAssignment = fallbackAssignmentForUser || getCurrentAssignment(task?.taskAssignments);
          const isUserAssignedFallback = userName === fallbackAssignment?.todoAppUser.maccount.toLowerCase();
          
          // If user is not assigned to the task, always use bulk pause with all started executors
          if (!isUserAssignedFallback) {
            if (startedExecutors.length === 0) {
              console.warn('No started executors found to pause');
              setLoading(false);
              return;
            }
            return dispatch(pauseTaskBulk({ taskId, mAccounts: startedExecutors })).then(() => {
              return dispatch(fetchTask(taskId)).then(() => {
                setLoading(false);
              })
            })
          } else if (startedExecutors.length > 1) {
            return dispatch(pauseTaskBulk({ taskId, mAccounts: startedExecutors })).then(() => {
              return dispatch(fetchTask(taskId)).then(() => {
                setLoading(false);
              })
            })
          } else {
            return dispatch(pauseTask(taskId)).then(() => {
              return dispatch(fetchTask(taskId)).then(() => {
                setLoading(false);
              })
            })
          }
        }
      },
    },
    release: {
      disabled: ((loading !== 'RELEASE') && loading !== false) || taskStatus === "Finished" || taskStatus === "Aborted",
      isLoading: loading === 'RELEASE',
      visible: isAssingedToCurrentUser,
      action: async (id) => {
        const taskId = (typeof id === 'number')? id : task.id;
        setLoading('RELEASE');
        
        try {
          // Refresh task data to get the most current active executors
          const refreshedTask = await dispatch(fetchTask(taskId));
          const currentTask = refreshedTask.payload || task;
          
          // Get all active assignees for the task using refreshed data
          const activeAssignments = getActiveAssignments(currentTask?.taskAssignments);
          const activeExecutors = getActiveExecutors(currentTask?.taskAssignments);
          console.log('Releasing task with active executors:', activeExecutors);
          
          // Check if there are multiple active assignees
          if (activeAssignments.length > 1) {
            // Use bulk release with all active assignees
            return dispatch(releaseTaskBulk({ taskId, mAccounts: activeExecutors })).then(() => {
              return dispatch(fetchTask(taskId)).then(() => {
                // Refresh task list to update filters (task status may have changed to "Created")
                dispatch(fetchTasks());
                setLoading(false);
              })
            })
          } else {
            // Use single release
            return dispatch(releaseTask(taskId)).then(() => {
              return dispatch(fetchTask(taskId)).then(() => {
                // Refresh task list to update filters (task status may have changed to "Created")
                dispatch(fetchTasks());
                setLoading(false);
              })
            })
          }
        } catch (error) {
          console.error('Error refreshing task data before release:', error);
          // Fallback to using the original task data if refresh fails
          const activeAssignments = getActiveAssignments(task?.taskAssignments);
          const activeExecutors = getActiveExecutors(task?.taskAssignments);
          
          if (activeAssignments.length > 1) {
            return dispatch(releaseTaskBulk({ taskId, mAccounts: activeExecutors })).then(() => {
              return dispatch(fetchTask(taskId)).then(() => {
                // Refresh task list to update filters (task status may have changed to "Created")
                dispatch(fetchTasks());
                setLoading(false);
              })
            })
          } else {
            return dispatch(releaseTask(taskId)).then(() => {
              return dispatch(fetchTask(taskId)).then(() => {
                // Refresh task list to update filters (task status may have changed to "Created")
                dispatch(fetchTasks());
                setLoading(false);
              })
            })
          }
        }
      },
    },
    claim: {
      disabled: (loading !== 'CLAIM') && loading !== false,
      isLoading: loading === 'CLAIM',
      visible: !isAssingedToCurrentUser,
      action: async () => {
        const taskId = task.id;
        setLoading('CLAIM');
        
        try {
          // Check if task has any started assignments before claiming
          const hasStartedAssignment = hasAnyStartedAssignment(task?.taskAssignments);
          
          // Claim the task first
          await dispatch(claimTask(taskId));
          
          // Refresh task data to get updated assignments after claim
          const refreshedTask = await dispatch(fetchTask(taskId));
          const currentTask = refreshedTask.payload || task;
          
          // If task was already started, also start it ONLY for the new user (OPTIE B)
          // This ensures time registration is created ONLY for the new claimer, not for all users
          if (hasStartedAssignment) {
            // Only start for the current user (the one who just claimed)
            // Use single start, not bulk, to avoid restarting other users' time registrations
            await dispatch(startTask(taskId));
            
            // Refresh task data again after starting
            await dispatch(fetchTask(taskId));
          }
          
          setLoading(false);
        } catch (error) {
          console.error('Error in claim action:', error);
          setLoading(false);
          throw error;
        }
      },
    },
  }
}
